import { Controller } from "@hotwired/stimulus"

const ACTION_WIDTH = 80
const REVEAL_THRESHOLD = 40
const DELETE_THRESHOLD = 160

let currentlyRevealed = null

export default class extends Controller {
  static targets = ["content", "action"]
  static values = { url: String }

  connect() {
    this.startX = 0
    this.startY = 0
    this.currentX = 0
    this.isDragging = false
    this.isScrolling = null
    this.isRevealed = false

    // Allow vertical scroll to pass through to the browser,
    // but let us handle horizontal swipes in JS
    this.contentTarget.style.touchAction = "pan-y"

    this._outsideHandler = (e) => {
      if (this.isRevealed && !this.element.contains(e.target)) {
        this.snapBack()
      }
    }
    document.addEventListener("touchstart", this._outsideHandler, { passive: true })
  }

  disconnect() {
    document.removeEventListener("touchstart", this._outsideHandler)
    // Drop the module-level reference if it points at this (now-disconnected)
    // controller, so it doesn't pin the instance + element after a DOM swap.
    if (currentlyRevealed === this) currentlyRevealed = null
  }

  // ── Touch events (iOS device + Simulator) ───────────────────────────────

  touchStart(e) {
    const t = e.touches[0]
    this.startX = t.clientX
    this.startY = t.clientY
    this.currentX = this.isRevealed ? -ACTION_WIDTH : 0
    this.isDragging = true
    this.isScrolling = null
    this.contentTarget.style.transition = "none"
  }

  touchMove(e) {
    if (!this.isDragging) return
    const t = e.touches[0]
    const deltaX = t.clientX - this.startX
    const deltaY = t.clientY - this.startY

    if (this.isScrolling === null) {
      if (Math.abs(deltaX) < 4 && Math.abs(deltaY) < 4) return
      this.isScrolling = Math.abs(deltaY) > Math.abs(deltaX)
    }

    if (this.isScrolling) return

    // Only allow leftward swipe (or rightward to close)
    const base = this.isRevealed ? -ACTION_WIDTH : 0
    const offset = Math.min(0, Math.max(-300, base + deltaX))
    this.currentX = offset
    this.contentTarget.style.transform = `translateX(${offset}px)`
  }

  touchEnd() {
    if (!this.isDragging) return
    this.isDragging = false
    if (this.isScrolling) return
    this._settle()
  }

  // ── Mouse events (desktop browser testing) ──────────────────────────────

  mouseDown(e) {
    if (e.button !== 0) return
    this.startX = e.clientX
    this.startY = e.clientY
    this.currentX = this.isRevealed ? -ACTION_WIDTH : 0
    this.isDragging = true
    this.isScrolling = null
    this.contentTarget.style.transition = "none"

    this._mouseMoveHandler = this.mouseMove.bind(this)
    this._mouseUpHandler = this.mouseUp.bind(this)
    document.addEventListener("mousemove", this._mouseMoveHandler)
    document.addEventListener("mouseup", this._mouseUpHandler)
  }

  mouseMove(e) {
    if (!this.isDragging) return
    const deltaX = e.clientX - this.startX
    const deltaY = e.clientY - this.startY

    if (this.isScrolling === null) {
      if (Math.abs(deltaX) < 4 && Math.abs(deltaY) < 4) return
      this.isScrolling = Math.abs(deltaY) > Math.abs(deltaX)
    }

    if (this.isScrolling) return

    const base = this.isRevealed ? -ACTION_WIDTH : 0
    const offset = Math.min(0, Math.max(-300, base + deltaX))
    this.currentX = offset
    this.contentTarget.style.transform = `translateX(${offset}px)`
  }

  mouseUp() {
    document.removeEventListener("mousemove", this._mouseMoveHandler)
    document.removeEventListener("mouseup", this._mouseUpHandler)
    if (!this.isDragging) return
    this.isDragging = false
    if (this.isScrolling) return
    this._settle()
  }

  // ── Shared logic ─────────────────────────────────────────────────────────

  _settle() {
    const absX = Math.abs(this.currentX)
    if (absX >= DELETE_THRESHOLD) {
      this.animateAndDelete()
    } else if (absX >= REVEAL_THRESHOLD) {
      this.reveal()
    } else {
      this.snapBack()
    }
  }

  reveal() {
    if (currentlyRevealed && currentlyRevealed !== this) {
      currentlyRevealed.snapBack()
    }
    currentlyRevealed = this
    this.isRevealed = true
    this.contentTarget.style.transition = "transform 0.2s ease"
    this.contentTarget.style.transform = `translateX(-${ACTION_WIDTH}px)`
  }

  snapBack() {
    this.isRevealed = false
    if (currentlyRevealed === this) currentlyRevealed = null
    this.contentTarget.style.transition = "transform 0.2s ease"
    this.contentTarget.style.transform = "translateX(0)"
  }

  confirmDelete(e) {
    e.preventDefault()
    e.stopPropagation()
    this.animateAndDelete()
  }

  async animateAndDelete() {
    const el = this.element

    this.contentTarget.style.transition = "transform 0.2s ease"
    this.contentTarget.style.transform = "translateX(-100%)"

    await new Promise(r => setTimeout(r, 180))
    const height = el.offsetHeight
    el.style.overflow = "hidden"
    el.style.height = height + "px"
    el.style.transition = "height 0.2s ease, opacity 0.15s ease"

    requestAnimationFrame(() => {
      el.style.height = "0"
      el.style.opacity = "0"
    })

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.urlValue, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken }
    })

    await new Promise(r => setTimeout(r, 200))
    el.remove()
  }
}
